<template lang="pug">
section.fit-referrals.card
  TableTitle(title="Patient Navigation")
    template(#actions)
      BaseBtn.x-lg.blue(href="#", @click.prevent="openAddOverlay") ADD NEW
  .table
    .th
      .td(
        @click.prevent="changeListOrder('PatientNavigationName', fetchPatientNavigationSettingsList)"
        style="cursor: pointer;"
      )
        | ENTRY NAME
        i(style="font-weight: bolder;") {{ PatientNavigationNameColumnSortingIcon }}
      .td ACTIVE
      .td
    .tr(
      v-for="setting in patientNavigationSettings",
      :key="setting.id"
    )
      .td.bold {{ setting.name }}
      .td
        BaseToggle(
          @update:modelValue="toggleStatus(setting.id)"
          :modelValue="!setting.disabled"
        )
      .td
        BaseBtn.sm.blue(href="#", @click.prevent="openManageOverlay(setting)") MANAGE
  Pagination(
    v-if="pagination",
    :pagination="pagination",
    :reloadFn="fetchPatientNavigationSettingsList"
    @changePagination="changePagination"
  )
  AddPatientNavigationSettingOverlay(
    :isOpen="isAddOverlayOpen",
    @closeAddPatientNavigationSettingOverlay="closeAddOverlay"
  )
  ManagePatientNavigationSettingOverlay(
    :key="`${componentKey}-patient-navigation`"
    :isOpen="isManageOverlayOpen",
    :id="settingToManage?.id || null"
    @closeManagePatientNavigationSettingOverlay="closeManageOverlay"
  )
</template>

<script>
import { defineComponent } from "vue";
import TableTitle from "@/components/TableTitle.vue";
import Pagination from "@/components/Pagination.vue";
import BaseToggle from "@/components/BaseToggle.vue";
import BaseBtn from "@/components/BaseBtn.vue";
import AddPatientNavigationSettingOverlay from "@/components/system-settings/overlays/AddPatientNavigationSettingOverlay.vue";
import ManagePatientNavigationSettingOverlay from "@/components/system-settings/overlays/ManagePatientNavigationSettingOverlay.vue";
import listPagesMixin from "@/mixins/listPagesMixin";
import sortPatientNavigationSettings from "@/mixins/sortPatientNavigationSettings";
import { apiStatus, apiStatusComputedFactory } from "@/api";
import {
  fetchPatientNavigationSettings,
  activatePatientNavigationSetting,
  deactivatePatientNavigationSetting,
} from "@/api/patientNavigationSettingsApi";

const { IDLE, PENDING, SUCCESS, ERROR } = apiStatus;

export default defineComponent({
  components: {
    TableTitle,
    Pagination,
    BaseToggle,
    BaseBtn,
    AddPatientNavigationSettingOverlay,
    ManagePatientNavigationSettingOverlay,
  },
  mixins: [listPagesMixin, sortPatientNavigationSettings],
  data() {
    return {
      patientNavigationSettings: [],
      settingToManage: null,
      isAddOverlayOpen: false,
      isManageOverlayOpen: false,
      fetchStatus: IDLE,
      activateStatus: IDLE,
      deactivateStatus: IDLE,
    };
  },
  computed: {
    ...apiStatusComputedFactory(["fetchStatus"]),
  },
  methods: {
    openAddOverlay() {
      this.isAddOverlayOpen = true;
    },
    closeAddOverlay() {
      this.isAddOverlayOpen = false;
      this.fetchPatientNavigationSettingsList();
    },
    openManageOverlay(setting) {
      this.forceRerender();
      this.settingToManage = setting;
      this.isManageOverlayOpen = true;
    },
    closeManageOverlay() {
      this.isManageOverlayOpen = false;
      this.fetchPatientNavigationSettingsList();
    },
    async fetchPatientNavigationSettingsList() {
      this.fetchStatus = PENDING;
      const order = this.listOrder
        ? {
            by: this.listOrder.by,
            direction: this.listOrder.direction,
          }
        : null;

      try {
        const { data } = await fetchPatientNavigationSettings({
          params: {
            ...this.reqPagination,
            order,
          },
        });
        this.patientNavigationSettings = data.patientNavigationSettings;
        this.pagination = data.pagination;
        this.fetchStatus = SUCCESS;
      } catch (error) {
        console.log(error);
        this.fetchStatus = ERROR;
      }
    },
    async activateSetting(setting) {
      this.activateStatus = PENDING;
      try {
        await activatePatientNavigationSetting(String(setting.id), {
          patientNavigationSetting: setting,
        });
        this.activateStatus = SUCCESS;
        await this.fetchPatientNavigationSettingsList();
      } catch (error) {
        console.log(error);
        this.activateStatus = ERROR;
      }
    },
    async deactivateSetting(setting) {
      this.deactivateStatus = PENDING;
      try {
        await deactivatePatientNavigationSetting(String(setting.id), {
          patientNavigationSetting: setting,
        });
        this.deactivateStatus = SUCCESS;
        await this.fetchPatientNavigationSettingsList();
      } catch (error) {
        console.log(error);
        this.deactivateStatus = ERROR;
      }
    },
    toggleStatus(id) {
      const setting = this.patientNavigationSettings.find(
        (entry) => entry.id === id
      );
      if (!setting) return;
      if (setting.disabled) {
        return this.activateSetting(setting);
      }
      return this.deactivateSetting(setting);
    },
  },
  created() {
    this.fetchPatientNavigationSettingsList();
  },
});
</script>

<style scoped lang="scss">
.td {
  &:nth-child(1) {
    width: math.percentage(math.div(744, $tabContentCardWidth));
  }
  &:nth-child(2) {
    width: math.percentage(math.div(205, $tabContentCardWidth));
  }
  &:last-child {
    width: math.percentage(math.div(229, $tabContentCardWidth));
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }
}
</style>
